import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Pagination from '../../components/Pagination';
import Header2 from '../../components/Header2';
import api from '../../services/Api';
import Title from '../../components/Title';
import TransactionChart from '../../components/analytics/TransactionChart';
import { formatDate } from '../../utils/Date';

const Transaction = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);

  const { currentPage, entriesPerPage } = useSelector(state => state.pagination);
  const searchQuery = useSelector(state => state.search.query);

  const setStatus = statuses => {
    setSelectedStatuses(statuses);
  };

  const setIsVerified = types => {
    setSelectedTypes(types);
  };

  const resetFilter = () => {
    setSelectedStatuses([]);
    setSelectedTypes([]);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get('/subscriptions/payments');
        const uniqueTransactions = removeDuplicateTransactions(response.data.payments);
        setTransactions(uniqueTransactions);
        setFilteredTransactions(uniqueTransactions);
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, []);

  const removeDuplicateTransactions = transactions => {
    const transactionMap = {};
    const uniqueTransactions = [];

    transactions.forEach(transaction => {
      if (transaction.id && !transactionMap[transaction.id]) {
        transactionMap[transaction.id] = true;
        uniqueTransactions.push(transaction);
      }
    });

    return uniqueTransactions;
  };

  useEffect(() => {
    let filtered = transactions;

    if (searchQuery) {
      filtered = filtered.filter(transaction => {
        const emailMatch =
          transaction?.customer?.email?.toLowerCase().includes(searchQuery.toLowerCase()) || false;
        return emailMatch;
      });
    }

    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(transaction => {
        const successResponses = ['Successful', 'Approved', 'Transaction Successful'];
        const pendingResponses = [
          'Processing',
          'Pending',
          'In Progress',
          'Verification pending',
          'Pending OTP validation',
          'Authentication Required',
          '3DS Authentication Required',
        ];

        let statusValue;
        if (successResponses.includes(transaction?.gateway_response)) {
          statusValue = 'Active';
        } else if (pendingResponses.includes(transaction?.gateway_response)) {
          statusValue = 'Pending';
        } else {
          statusValue = 'Inactive';
        }

        return selectedStatuses.includes(statusValue);
      });
    }

    if (selectedTypes.length > 0) {
      filtered = filtered.filter(transaction => {
        return selectedTypes.includes(transaction.type || transaction.category);
      });
    }

    setFilteredTransactions(filtered);
  }, [transactions, searchQuery, selectedStatuses, selectedTypes]);

  const getTransactionStatusInfo = gatewayResponse => {
    const successResponses = ['Successful', 'Approved', 'Transaction Successful'];

    const pendingResponses = [
      'Processing',
      'Pending',
      'In Progress',
      'Verification pending',
      'Pending OTP validation',
      'Authentication Required',
      '3DS Authentication Required',
    ];

    if (successResponses.includes(gatewayResponse)) {
      return {
        className: 'bg-[#E7F7EF] text-[#0FAF62]',
        display: gatewayResponse || 'Successful',
      };
    } else if (pendingResponses.includes(gatewayResponse)) {
      return {
        className: 'bg-[#FFF8E1] text-[#FFA000]',
        display: 'Pending',
      };
    } else {
      return {
        className: 'bg-[#FEE7E6] text-[#F44336]',
        display: 'Failed',
      };
    }
  };

  const totalEntries = filteredTransactions?.length || 0;
  const paginatedTransactions =
    filteredTransactions?.slice((currentPage - 1) * entriesPerPage, currentPage * entriesPerPage) ||
    [];

  return (
    <div className="p-4 sm:ml-64 w-[80%] bg-[#F4F5F6] font-nohemi">
      <div className="flex items-center ">
        <Title title="Transaction" />
      </div>
      <div>
        <Header2 setStatus={setStatus} setIsVerified={setIsVerified} resetFilter={resetFilter} />
      </div>
      <div className="p-6 bg-white rounded-lg">
        <div className="relative mx-4 mt-6 overflow-x-auto rounded-lg">
          <table className="w-full mx-auto text-left border">
            <thead>
              <tr className="font-light text-base text-[#000000] bg-[#F5F6FA]">
                <td className="p-3 pl-6">ID</td>
                <td className="p-3">Email</td>
                <td className="p-3">Date</td>
                <td className="p-3">Amount</td>
                <td className="p-3 pl-8">Status</td>
              </tr>
            </thead>
            <tbody>
              {paginatedTransactions.map((transaction, index) => {
                const statusInfo = getTransactionStatusInfo(transaction?.gateway_response);
                const uniqueKey = `${transaction?.id || ''}-${index}`;

                return (
                  <tr key={uniqueKey} className="border-b font-light text-sm text-[#4A5154]">
                    <td className="px-6 py-2">{transaction?.id}</td>
                    <td className="p-2">{transaction?.customer?.email}</td>
                    <td className="p-2">{formatDate(transaction?.paid_at)}</td>
                    <td className="p-2">
                      $ {transaction?.amount ? (transaction.amount / 100).toLocaleString() : ''}
                    </td>
                    <td className="">
                      <div className="flex items-center justify-start">
                        <button
                          className={`rounded inline-flex items-center justify-center px-2.5 py-1 m-4 text-center text-xs font-medium focus:ring-0 ${statusInfo.className}`}>
                          {statusInfo.display}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination totalEntries={totalEntries} />
      </div>
      <div className="p-6 mt-4 bg-white rounded-lg">
        <div className="bg-white rounded-lg h-[400px] w-full">
          <div className="flex flex-row items-center justify-between w-full px-2 mt-4 bg-white">
            <div className="w-full">
              <TransactionChart />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Transaction;
